import { useMemo, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import "./feedbackForm.css";
import { useTranslation } from "react-i18next";
import { api } from "../../../../mock/api";

export type FeedbackFormValues = {
  fullName: string;
  position: string;
  phone: string;
  email?: string;
  comment?: string;
  tender?: boolean;
};

export type FeedbackFormCopy = {
  labels?: {
    fullName?: string;
    position?: string;
    phone?: string;
    email?: string;
    comment?: string;
    tender?: string;
  };
  placeholders?: {
    fullName?: string;
    position?: string;
    phone?: string;
    email?: string;
    comment?: string;
  };
  errors?: {
    required?: string;
    phoneShort?: string;
    email?: string;
    captcha?: string;
  };
  buttons?: {
    send?: string;
    sending?: string;
  };
  successIconAlt?: string;
  thanksText?: string;
  submitError?: string;
};

type Props = {
  submitLabel?: string;
  showTender?: boolean;
  thanksText?: string;
  onSuccess?: (values: FeedbackFormValues) => void;
  initialValues?: Partial<FeedbackFormValues>;
  className?: string;
  copy?: FeedbackFormCopy;
};

type Errors = Partial<Record<keyof FeedbackFormValues | "captcha", string>>;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
const normalizePhone = (v: string) => v.replace(/[^\d+]/g, "").trim();
const captchaSiteKey = process.env.REACT_APP_RECAPTCHA || "";

export const FeedbackForm = ({
  submitLabel,
  showTender = true,
  thanksText,
  onSuccess,
  initialValues,
  className,
  copy,
}: Props) => {
  const { t } = useTranslation();
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  const [values, setValues] = useState<FeedbackFormValues>({
    fullName: initialValues?.fullName ?? "",
    position: initialValues?.position ?? "",
    phone: initialValues?.phone ?? "",
    email: initialValues?.email ?? "",
    comment: initialValues?.comment ?? "",
    tender: initialValues?.tender ?? false,
  });

  const [captchaToken, setCaptchaToken] = useState<string>("");
  const [errors, setErrors] = useState<Errors>({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const label = (
    key: keyof NonNullable<FeedbackFormCopy["labels"]>,
    fallback: string,
  ) => copy?.labels?.[key] ?? fallback;

  const placeholder = (
    key: keyof NonNullable<FeedbackFormCopy["placeholders"]>,
    fallback: string,
  ) => copy?.placeholders?.[key] ?? fallback;

  const errorCopy = (
    key: keyof NonNullable<FeedbackFormCopy["errors"]>,
    fallback: string,
  ) => copy?.errors?.[key] ?? fallback;

  const btnText = submitLabel ?? copy?.buttons?.send ?? t("common.send");
  const sendingText = copy?.buttons?.sending ?? t("common.sending");
  const okText = thanksText ?? copy?.thanksText ?? t("forms.thanksFallback");
  const successIconAlt =
    copy?.successIconAlt ?? t("forms.successIconAlt", "Message sent");
  const submitErrorText = String(
    copy?.submitError ?? t("notifications.error", "Request failed"),
  );

  const validators = useMemo(() => {
    return {
      fullName: (v: string) =>
        v.trim().length ? "" : errorCopy("required", t("errors.required")),
      position: (v: string) =>
        v.trim().length ? "" : errorCopy("required", t("errors.required")),
      phone: (v: string) => {
        const n = normalizePhone(v);
        if (!n.length) return errorCopy("required", t("errors.required"));
        const digits = n.replace(/[^\d]/g, "");
        if (digits.length < 10)
          return errorCopy("phoneShort", t("errors.phoneShort"));
        return "";
      },
      email: (v?: string) => {
        const val = (v ?? "").trim();
        if (!val) return "";
        if (!emailRegex.test(val)) return errorCopy("email", t("errors.email"));
        return "";
      },
      captcha: (v?: string) =>
        (v ?? "").trim().length
          ? ""
          : errorCopy(
              "captcha",
              t("errors.captcha", "Confirm you are not a robot"),
            ),
    };
  }, [copy, t]);

  const validateAll = (): boolean => {
    const next: Errors = {};

    const e1 = validators.fullName(values.fullName);
    if (e1) next.fullName = e1;

    const e2 = validators.position(values.position);
    if (e2) next.position = e2;

    const e3 = validators.phone(values.phone);
    if (e3) next.phone = e3;

    const e4 = validators.email(values.email);
    if (e4) next.email = e4;

    const e5 = validators.captcha(captchaToken);
    if (e5) next.captcha = e5;

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const setField = <K extends keyof FeedbackFormValues>(
    key: K,
    val: FeedbackFormValues[K],
  ) => {
    setValues((p) => ({ ...p, [key]: val }));
    setSubmitError("");

    setErrors((prev) => {
      const next = { ...prev };
      if (key === "fullName") {
        next.fullName = validators.fullName(String(val ?? ""));
      }
      if (key === "position") {
        next.position = validators.position(String(val ?? ""));
      }
      if (key === "phone") {
        next.phone = validators.phone(String(val ?? ""));
      }
      if (key === "email") {
        next.email = validators.email(String(val ?? ""));
      }
      return next;
    });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");
    if (!validateAll()) return;

    setLoading(true);
    try {
      await api.sendFeedback({
        ...values,
        phone: normalizePhone(values.phone),
        captchaToken,
      });

      setSent(true);
      onSuccess?.(values);
      setCaptchaToken("");
      recaptchaRef.current?.reset();
    } catch (_error) {
      setSubmitError(submitErrorText);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className={`oc-ff__thanks ${className ?? ""}`}>
        <img
          className="oc-ff__thanksIcon"
          src="/assets/icons/emailcheck.svg"
          alt={successIconAlt}
        />
        <div className="oc-ff__thanksText">{okText}</div>
      </div>
    );
  }

  return (
    <form className={`oc-ff ${className ?? ""}`} onSubmit={submit} noValidate>
      <div className="oc-ff__field">
        <label className="oc-formLabel">
          {label("fullName", t("forms.fullName"))}{" "}
          <span className="oc-ff__req">*</span>
        </label>
        <input
          className={`oc-formInput ${
            errors.fullName ? "oc-ff__input--error" : ""
          }`}
          value={values.fullName}
          onChange={(e) => setField("fullName", e.target.value)}
          placeholder={placeholder("fullName", t("placeholders.fullName"))}
          autoComplete="name"
        />
        {errors.fullName ? (
          <div className="oc-formError">{errors.fullName}</div>
        ) : null}
      </div>

      <div className="oc-ff__field">
        <label className="oc-formLabel">
          {label("position", t("forms.position"))}{" "}
          <span className="oc-ff__req">*</span>
        </label>
        <input
          className={`oc-formInput ${
            errors.position ? "oc-ff__input--error" : ""
          }`}
          value={values.position}
          onChange={(e) => setField("position", e.target.value)}
          placeholder={placeholder("position", t("placeholders.position"))}
        />
        {errors.position ? (
          <div className="oc-formError">{errors.position}</div>
        ) : null}
      </div>

      <div className="oc-ff__field">
        <label className="oc-formLabel">
          {label("phone", t("forms.phone"))}{" "}
          <span className="oc-ff__req">*</span>
        </label>
        <input
          className={`oc-formInput ${
            errors.phone ? "oc-ff__input--error" : ""
          }`}
          value={values.phone}
          onChange={(e) => setField("phone", e.target.value)}
          placeholder={placeholder("phone", t("placeholders.phone"))}
          inputMode="tel"
          autoComplete="tel"
        />
        {errors.phone ? (
          <div className="oc-formError">{errors.phone}</div>
        ) : null}
      </div>

      <div className="oc-ff__field">
        <label className="oc-formLabel">
          {label("email", t("forms.email"))}
        </label>
        <input
          className={`oc-formInput ${
            errors.email ? "oc-ff__input--error" : ""
          }`}
          value={values.email ?? ""}
          onChange={(e) => setField("email", e.target.value)}
          placeholder={placeholder("email", t("placeholders.email"))}
          inputMode="email"
          autoComplete="email"
        />
        {errors.email ? (
          <div className="oc-formError">{errors.email}</div>
        ) : null}
      </div>

      <div className="oc-ff__field">
        <label className="oc-formLabel">
          {label("comment", t("forms.comment"))}
        </label>
        <textarea
          className="oc-formTextarea"
          value={values.comment ?? ""}
          onChange={(e) => setField("comment", e.target.value)}
          placeholder={placeholder("comment", t("placeholders.comment"))}
        />
      </div>

      {showTender ? (
        <label className="oc-ff__check">
          <input
            type="checkbox"
            checked={!!values.tender}
            onChange={(e) => setField("tender", e.target.checked)}
          />
          <span className="oc-ff__checkBox" aria-hidden="true" />
          <span className="oc-ff__checkText">
            {label("tender", t("forms.tender"))}
          </span>
        </label>
      ) : null}

      <div className="oc-ff__field oc-ff__captcha">
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={captchaSiteKey}
          onChange={(token) => {
            const nextToken = token ?? "";
            setCaptchaToken(nextToken);
            setErrors((prev) => ({
              ...prev,
              captcha: validators.captcha(nextToken),
            }));
          }}
          onExpired={() => {
            setCaptchaToken("");
            setErrors((prev) => ({
              ...prev,
              captcha: validators.captcha(""),
            }));
          }}
        />
        {errors.captcha ? (
          <div className="oc-formError">{errors.captcha}</div>
        ) : null}
      </div>

      {submitError ? <div className="oc-formError">{submitError}</div> : null}

      <button
        className="oc-btn oc-btn--primary oc-btn--lg oc-ff__submit"
        type="submit"
        disabled={loading}
      >
        {loading ? sendingText : btnText}
      </button>
    </form>
  );
};
